import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CircleAlert,
  CircleDollarSign,
  Info,
  LoaderCircle,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatUsd } from "../utils/money";
import { engagementApi } from "../api/engagement";
import { walletApi } from "../api/wallet";
import { useCopy } from "../i18n/copy";
import { useSession } from "../store/session";
import { sanitizeHtml } from "../utils/sanitizeHtml";

function NoticePage({
  type,
  title,
  icon: Icon,
}: {
  type: number;
  title: string;
  icon: typeof Info;
}) {
  const { isPreview } = useSession();
  const { tr } = useCopy();
  const query = useQuery({
    queryKey: ["notice", type],
    queryFn: () => engagementApi.notice(type),
    enabled: !isPreview,
  });
  return (
    <main className="page info-page">
      <Link className="back-link" to="/account">
        <ArrowLeft /> {tr("Volver a mi cuenta")}
      </Link>
      <header className="page-title-row">
        <div>
          <p className="eyebrow">GOP ALA Ecuador</p>
          <h1>{tr(title)}</h1>
        </div>
      </header>
      {isPreview ? (
        <div className="empty-state">
          <Icon />
          <h2>{tr("Iniciar sesión")}</h2>
          <Link className="button primary" to="/auth/login">
            {tr("Iniciar sesión")} <ArrowRight />
          </Link>
        </div>
      ) : query.isLoading ? (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando…")}
        </div>
      ) : query.isError ? (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos cargar el contenido.")}
        </div>
      ) : (
        <article
          className="notice-card"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(query.data?.content || ""),
          }}
        />
      )}
    </main>
  );
}

export function RecruitmentPage() {
  return <NoticePage type={7} title="Reclutamiento" icon={BriefcaseBusiness} />;
}

export function AboutPage() {
  return <NoticePage type={1} title="Sobre GOP ALA" icon={Info} />;
}

export function CompanyPage() {
  return <NoticePage type={12} title="Perfil de la empresa" icon={Building2} />;
}

const recordNames: Record<string, string> = {
  "7": "Beneficio de equipo",
  "16": "Beneficio de invitación",
  "31": "Ingreso de potencia de cómputo",
};

export function TeamIncomePage() {
  const { user, isPreview } = useSession();
  const { tr } = useCopy();
  const query = useInfiniteQuery({
    queryKey: ["team-income"],
    queryFn: ({ pageParam }) => walletApi.fundRecordsByType(3, pageParam, 30),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === 30 ? pages.length + 1 : undefined,
    enabled: !isPreview,
  });
  const records = query.data?.pages.flat() || [];
  return (
    <main className="page team-income-page">
      <Link className="back-link" to="/account">
        <ArrowLeft /> {tr("Volver a mi cuenta")}
      </Link>
      <header className="page-title-row">
        <div>
          <p className="eyebrow">{tr("Comunidad GOP ALA")}</p>
          <h1>{tr("Ingresos del equipo")}</h1>
        </div>
      </header>
      <section className="team-income-balance">
        <span>
          <TrendingUp />
        </span>
        <div>
          <small>{tr("Saldo del equipo")}</small>
          <strong>{formatUsd(user.teamBalance)}</strong>
        </div>
      </section>
      {isPreview ? (
        <div className="empty-state">
          <CircleDollarSign />
          <h2>{tr("Iniciar sesión")}</h2>
          <Link className="button primary" to="/auth/login">
            {tr("Iniciar sesión")} <ArrowRight />
          </Link>
        </div>
      ) : (
        <section className="team-income-list">
          {query.isLoading && (
            <div className="loading-row">
              <LoaderCircle className="spin" /> {tr("Cargando…")}
            </div>
          )}
          {query.isError && (
            <div className="form-feedback error">
              <CircleAlert /> {tr("No pudimos cargar los movimientos.")}
            </div>
          )}
          {records.map((record, index) => {
            const difference =
              Number(record.after_amount) - Number(record.before_amount);
            return (
              <article key={`${record.create_time}-${index}`}>
                <span className="income-record-icon">
                  <CircleDollarSign />
                </span>
                <div>
                  <strong>
                    {tr(
                      recordNames[String(record.type)] || String(record.type),
                    )}
                  </strong>
                  <time>{record.create_time}</time>
                  <div className="income-balance-flow">
                    <span>
                      <small>{tr("Anterior")}</small>
                      <b>{formatUsd(Number(record.before_amount))}</b>
                    </span>
                    <i />
                    <span>
                      <small>{tr("Actual")}</small>
                      <b>{formatUsd(Number(record.after_amount))}</b>
                    </span>
                  </div>
                </div>
                <em className={difference >= 0 ? "positive" : "negative"}>
                  {difference >= 0 ? "+" : "-"}
                  {formatUsd(Math.abs(difference))}
                </em>
              </article>
            );
          })}
          {!query.isLoading && !records.length && (
            <div className="empty-state">
              <CircleDollarSign />
              <h2>{tr("No hay registros")}</h2>
            </div>
          )}
          {query.hasNextPage && (
            <button
              className="button secondary full"
              disabled={query.isFetchingNextPage}
              onClick={() => query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? (
                <LoaderCircle className="spin" />
              ) : null}
              {tr("Cargar más")}
            </button>
          )}
        </section>
      )}
    </main>
  );
}
